/**
 * Correlation / W3C trace-context helpers for the shell.
 *
 * Every browser session gets one stable correlation id and a W3C trace id so
 * UI telemetry and outbound gateway requests can be joined to server-side
 * traces. The id survives a soft reload via sessionStorage but is fresh per
 * tab/session so distinct operator sessions don't collide.
 *
 * `traceparent` follows the W3C Trace Context format:
 *   version "-" trace-id "-" parent-id "-" flags
 *   00-<32 hex>-<16 hex>-01
 * The gateway's tracing middleware continues the trace from this header.
 */

const CORRELATION_KEY = 'tyndo.correlation_id';

function randomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < bytes; i += 1) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

let cached: string | null = null;

/** Stable per-session correlation id (a 16-byte / 32-hex trace id). */
export function getCorrelationId(): string {
  if (cached) return cached;
  if (typeof window !== 'undefined') {
    try {
      const existing = window.sessionStorage.getItem(CORRELATION_KEY);
      if (existing) {
        cached = existing;
        return existing;
      }
    } catch {
      /* sessionStorage may be unavailable (privacy mode) — fall through. */
    }
  }
  cached = randomHex(16);
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(CORRELATION_KEY, cached);
    } catch {
      /* ignore */
    }
  }
  return cached;
}

/**
 * Build the headers to attach to every outbound gateway request so the call
 * can be correlated server-side. A fresh span (parent) id is minted per call;
 * the trace id is the stable session correlation id.
 */
export function correlationHeaders(): Record<string, string> {
  const traceId = getCorrelationId();
  const spanId = randomHex(8);
  return {
    'x-correlation-id': traceId,
    traceparent: `00-${traceId}-${spanId}-01`,
  };
}
